import { Typography } from '@mui/material'
import styles from './riskAlert.module.scss'
import { countUnreadNotifications, deleteNotification, readNotification } from '../../features/notificationSlice'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ButtonCustom from '../atoms/button/ButtonCustom'
import { getTimePassed } from '../../util/getTime'
import StatusBtn from '../atoms/status/StatusBtn'
import { getRiskAlertSeverity } from '../../util/notificationIconHelper'

const severityConfig = {
	high: {
		color: '#F44336',
		borderColor: '#F44336',
		bgColor: '#FEF8F8',
		label: 'High',
		variant: 'error',
		icon: '/images/alert-triangle-red.svg'
	},
	medium: {
		color: '#FF9B21',
		borderColor: '#FF9B21',
		bgColor: '#FFF5E9',
		label: 'Medium',
		variant: 'warning',
		icon: '/images/alert-triangle-orange.svg'
	},
	low: {
		color: '#2563eb',
		borderColor: '#bfdbfe',
		bgColor: '#F5F9FF',
		label: 'Low',
		variant: 'info',
		icon: '/images/alert-triangle-blue.svg'
	}
}

const getVariantByLabel = (label) => {
	switch (label) {
		case 'High':
			return 'error';
		case 'Medium':
			return 'pending';
		default:
			return 'inprogress';
	}
};


const getIcon = (severity) => {
	const config = severityConfig[severity] || severityConfig.medium
	return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
			<path d="M10 7.5V11.6667" stroke="${config.color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
			<path d="M9.99956 17.8414H4.94956C2.05789 17.8414 0.849558 15.7747 2.24956 13.2497L4.84956 8.56641L7.29956 4.16641C8.78289 1.49141 11.2162 1.49141 12.6996 4.16641L15.1496 8.57474L17.7496 13.2581C19.1496 15.7831 17.9329 17.8497 15.0496 17.8497H9.99956V17.8414Z" stroke="${config.color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
			<path d="M9.99512 14.168H10.0026" stroke="${config.color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>`
}

export const RiskAlertCard = ({ notification, index }) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const severity = getRiskAlertSeverity(notification)
	const config = severityConfig[severity] || severityConfig.medium
	const hasLink = Boolean(notification?.link)
	const buttonLabel =
		notification?.buttonText ||
		notification?.button_text ||
		'View'
	const actionSteps = (notification?.actionSteps || []).length > 0
		? notification.actionSteps
		: hasLink ? [{ label: buttonLabel, link: notification.link }] : []
	const isUnread = notification?.status === 'unread'

	const handleActionClick = (e, step) => {
		e.stopPropagation()
		if (isUnread) {
			dispatch(readNotification(notification._id))
			dispatch(countUnreadNotifications())
		}
		if (step?.link) {
			navigate(step.link)
		}
	}

	const handleDismiss = (e) => {
		e.stopPropagation()
		dispatch(deleteNotification(notification._id))
		dispatch(countUnreadNotifications())
	}

	return (
		<li
			className={`${styles.riskAlertCard} ${isUnread ? styles.unread : ''}`}
			key={notification?._id || index}
			style={{
				borderLeft: isUnread ? `4px solid ${config.borderColor}` : '1px solid #DFE4EB',
				borderColor: isUnread ? config.borderColor : '#DFE4EB',
				backgroundColor: isUnread ? config.bgColor : '#ffffff'
			}}
		>
			<div className={styles.mainContainer}>
				{/* Icon */}
				<div
					className={styles.iconContainer}
					style={{
						backgroundColor: !isUnread ? config.bgColor : '#ffffff'
					}}
					dangerouslySetInnerHTML={{ __html: getIcon(severity) }}
				/>

				{/* Content */}
				<div className={styles.contentContainer}>
					{/* Header: Title + Badge + Time */}
					<div className={styles.headerRow}>
						<div className={styles.titleRow}>
							<Typography variant="h4" className={styles.title}>
								Risk Alert
							</Typography>
							<StatusBtn label={config.label} variant={getVariantByLabel(config.label)} />
						</div>
						<div className={styles.timeContainer}>
							<Typography variant="body2" className={styles.time}>
								{getTimePassed(notification?.createdAt)}
							</Typography>
						</div>
					</div>

					{/* Message */}
					<Typography variant="body1" className={styles.message}>
						{notification?.message}
					</Typography>

					{/* Urgency */}
					{notification?.urgencyText && (
						<div className={styles.urgency}>
							<img src="/images/clock-icon.svg" alt="Clock" className={styles.clockIcon} />
							<Typography variant="body2" className={styles.urgencyText}>
								{notification.urgencyText}
							</Typography>
						</div>
					)}

					{/* Actions */}
					{actionSteps.length > 0 && (
						<div className={styles.actionsContainer}>
							<Typography variant="body2" className={styles.actionsLabel}>
								Recommended Actions:
							</Typography>
							<div className={styles.actionButtons}>
								{actionSteps.map((step, idx) => (
									<ButtonCustom
										key={idx}
										variant={idx === 0 ? 'default' : 'outlined'}
										size="sm"
										onClick={(e) => handleActionClick(e, step)}
										className={`${styles.actionBtn} ${idx === 0 ? styles.primary : styles.secondary}`}
									>
										{step.label}
									</ButtonCustom>
								))}
								<button onClick={handleDismiss} className={styles.dismissLink}>
									× Dismiss
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</li>
	)
}
